import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { MdLocalOffer, MdArrowBack } from "react-icons/md";
import { BaseUrl } from "../config/api";
import "../assets/styles/Checkout.css";
import { toast } from "react-toastify";

const Checkout = () => {
  const { cartItems, selectedItems, removeFromCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [showStripePopup, setShowStripePopup] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);

  const savedAddress =
    JSON.parse(localStorage.getItem("checkoutFormData")) || {};

  // Static bank details
  const bankDetails = {
    bankName: "Your Bank Name",
    accountNo: "1234567890",
    ifsc: "IFSC1234",
    branch: "Your Branch Name",
  };

  const calculateCouponDiscount = (coupon) => {
    if (!coupon) return 0;

    const applicableItems = cartItems.filter(
      (item) =>
        selectedItems[item.id] &&
        (!coupon.productIds.length || coupon.productIds.includes(item.id))
    );

    const applicableTotal = applicableItems.reduce(
      (total, item) =>
        total +
        parseFloat(item.price.replace("$", "").replace(/,/g, "")) *
        item.quantity,
      0
    );

    let discountAmount = 0;
    if (coupon.type === "Percentage") {
      discountAmount = Math.round((applicableTotal * coupon.typeValue) / 100);
    } else {
      discountAmount = Math.min(coupon.typeValue, applicableTotal);
    }

    return discountAmount;
  };

  const selectedItemsCount = cartItems.filter(
    (item) => selectedItems[item.id]
  ).length;

  const formatPrice = (price) => {
    return parseFloat(price)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const totalMRP = cartItems.reduce(
    (total, item) =>
      selectedItems[item.id]
        ? total + parseFloat(item.sellingPrice) * item.quantity
        : total,
    0
  );

  const totalSellingPrice = cartItems.reduce(
    (total, item) =>
      selectedItems[item.id]
        ? total + parseFloat(item.sellingPrice) * item.quantity
        : total,
    0
  );

  const discountAmount = totalMRP - totalSellingPrice;
  const discountPercentage = Math.round((discountAmount / totalMRP) * 100);

  const platformFee = 0;
  const shippingFee = 0;

  const couponDiscount = calculateCouponDiscount(selectedCoupon);

  const finalTotal =
    totalSellingPrice + platformFee + shippingFee - couponDiscount;

  const handlePlaceOrder = async () => {
    if (selectedItemsCount === 0) {
      setShowErrorPopup(true);
      return;
    }

    if (paymentMethod === "online") {
      setShowStripePopup(true);
      return;
    }

    try {
      // Prepare order items data
      const orderItems = cartItems
        .filter((item) => selectedItems[item.id])
        .map((item) => ({
          product: item.id,
          name: item.name,
          qty: item.quantity || 1,
          price: parseFloat(item.price.replace("$", "").replace(/,/g, "")),
          image:
            Array.isArray(item.images) && item.images.length > 0
              ? item.images[0]
              : item.image || null,
        }));

      // Ensure all required address fields are present
      const shippingAddress = {
        firstName: savedAddress.firstName,
        lastName: savedAddress.lastName,
        email: savedAddress.email,
        addressLine1: savedAddress.addressLine1,
        addressLine2: savedAddress.addressLine2 || "",
        city: savedAddress.city,
        pincode: savedAddress.postalCode,
        state: savedAddress.state,
        phoneNumber: savedAddress.phoneCode + savedAddress.phoneNumber,
      };

      // Map the fees to match backend expectations
      const orderData = {
        orderItems,
        shippingAddress,
        paymentMethod,
        itemsPrice: totalMRP,
        taxPrice: platformFee, // platformFee maps to taxPrice in backend
        shippingPrice: shippingFee, // shippingFee maps to shippingPrice in backend
        totalPrice: finalTotal, // finalTotal maps to totalPrice in backend
        discountAmount,
        couponDiscount: couponDiscount || 0,
      };

      console.log("Sending order data:", orderData);

      const response = await fetch(`${BaseUrl}/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Server error:", errorData);
        throw new Error(errorData.message || "Failed to create order");
      }

      const createdOrder = await response.json();
      console.log("Order created successfully:", createdOrder);

      if (paymentMethod === "cod" || paymentMethod === "etf") {
        setShowOrderPopup(true);

        setTimeout(() => {
          cartItems.forEach((item) => {
            if (selectedItems[item.id]) {
              removeFromCart(item.id);
            }
          });
          localStorage.removeItem("checkoutFormData");
          localStorage.removeItem("selectedItems");
          navigate("/");
        }, 2000);
      } else {
        console.log("Implement online payment flow");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      setShowErrorPopup(true);
    }
  };

  const handleStripePayment = async () => {
    try {
      // Prepare order items data
      const orderItems = cartItems
        .filter((item) => selectedItems[item.id])
        .map((item) => ({
          product: item.id,
          name: item.name,
          qty: item.quantity || 1,
          price: parseFloat(item.price.replace("$", "").replace(/,/g, "")),
          image: Array.isArray(item.images) && item.images.length > 0
            ? item.images[0]
            : item.image || null,
        }));

      // Prepare complete order data
      const orderData = {
        orderItems,
        shippingAddress: {
          firstName: savedAddress.firstName,
          lastName: savedAddress.lastName,
          email: savedAddress.email,
          addressLine1: savedAddress.addressLine1,
          addressLine2: savedAddress.addressLine2 || "",
          city: savedAddress.city,
          pincode: savedAddress.postalCode,
          state: savedAddress.state,
          phoneNumber: savedAddress.phoneCode + savedAddress.phoneNumber,
        },
        paymentMethod: "online",
        itemsPrice: totalMRP,
        taxPrice: platformFee,
        shippingPrice: shippingFee,
        totalPrice: finalTotal,
        discountAmount,
        couponDiscount: couponDiscount || 0,
      };

      // Store complete order data in localStorage
      localStorage.setItem("pendingOrder", JSON.stringify(orderData));

      const response = await fetch(`${BaseUrl}/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productName: `Order: ${orderItems.map(item => item.name).join(", ")}`,
          price: finalTotal,
          quantity: 1,
          customerEmail: savedAddress.email,
        }),
      });

      if (!response.ok) {
        throw new Error("Payment initiation failed");
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error("Error processing payment:", error);
      setShowErrorPopup(true);
    }
  };

  const handleChangeAddress = () => {
    sessionStorage.setItem("fromPayment", "true");
    navigate("/address");
  };

  const handleRemoveSelectedItems = () => {
    setShowRemoveConfirmation(true);
  };

  const confirmRemove = () => {
    cartItems.forEach((item) => {
      if (selectedItems[item.id]) {
        removeFromCart(item.id);
      }
    });
    setShowRemoveConfirmation(false);
  };

  const handleMoveToWishlist = () => {
    setShowMoveConfirmation(true);
  };

  const confirmMove = () => {
    cartItems.forEach((item) => {
      if (selectedItems[item.id]) {
        if (!isInWishlist(item.id)) {
          addToWishlist(item);
        }
        removeFromCart(item.id);
      }
    });
    setShowMoveConfirmation(false);
  };

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${BaseUrl}/coupons`);
      const data = await response.json();
      setCoupons(data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const paymentStatus = urlParams.get("status");

    if (sessionId && paymentStatus) {
      if (paymentStatus === "success") {
        verifyPayment(sessionId);
      } else if (paymentStatus === "cancel") {
        setShowCancelPopup(true);
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await fetch(`${BaseUrl}/checkout-session/${sessionId}`);
      const session = await response.json();

      if (session.payment_status === "paid") {
        const pendingOrder = JSON.parse(localStorage.getItem("pendingOrder"));

        if (pendingOrder) {
          pendingOrder.items.forEach((item) => {
            removeFromCart(item.id);
          });

          localStorage.removeItem("pendingOrder");
          localStorage.removeItem("checkoutFormData");
          localStorage.removeItem("selectedItems");
        }

        setShowSuccessPopup(true);
        setPaymentSessionId(sessionId);
      } else {
        setShowErrorPopup(true);
      }
    } catch (err) {
      console.error("Payment verification failed:", err);
      setShowErrorPopup(true);
    }
  };

  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const handleApplyCoupon = (coupon) => {
    const discount = calculateCouponDiscount(coupon);
    if (discount > 0) {
      setSelectedCoupon(coupon);
      setShowCouponsModal(false);
      toast.success(`Coupon "${coupon.name}" applied successfully!`);
    } else {
      setSelectedCoupon(null);
    }
  };

  const PriceBreakdown = () => (
    <div className="checkout-payment-price-breakdown">
      <div className="checkout-price-row">
        <span>Amount</span>
        <span>${formatPrice(totalMRP)}</span>
      </div>

      {selectedCoupon && couponDiscount > 0 && (
        <div className="checkout-price-row">
          <span>Coupon Discount ({selectedCoupon.name})</span>
          <span className="checkout-discount">
            -${formatPrice(couponDiscount)}
          </span>
        </div>
      )}

      <div className="payment-price-row">
        <span>Tax</span>
        <span style={{ color: platformFee === 0 ? "#6F4685" : "#282c3f" }}>
          {platformFee === 0 ? "FREE" : `$${formatPrice(platformFee)}`}
        </span>
      </div>

      <div className="payment-price-row">
        <span>Shipping Fee</span>
        <span style={{ color: shippingFee === 0 ? "#6F4685" : "#282c3f" }}>
          {shippingFee === 0 ? "FREE" : `$${formatPrice(shippingFee)}`}
        </span>
      </div>

      <div className="payment-price-row payment-total">
        <span>Total Amount</span>
        <span>${formatPrice(finalTotal)}</span>
      </div>
    </div>
  );

  const CouponsModal = () => {
    const filteredCoupons = coupons.filter((coupon) => {
      if (!coupon.productIds || coupon.productIds.length === 0) {
        return true;
      }

      return cartItems.some(
        (item) => selectedItems[item.id] && coupon.productIds.includes(item.id)
      );
    });

    return (
      <div className="coupons-modal-overlay">
        <div className="coupons-modal">
          <button
            className="close-modal"
            onClick={() => setShowCouponsModal(false)}
          >
            ×
          </button>
          <h2>Available Coupons</h2>
          {filteredCoupons.length > 0 ? (
            <div className="coupons-list">
              {filteredCoupons.map((coupon) => (
                <div key={coupon._id} className="coupon-item">
                  <div className="coupon-details">
                    <h3>{coupon.name}</h3>
                    <p>
                      {coupon.type === "Percentage"
                        ? `${coupon.typeValue}% off`
                        : `$${formatPrice(coupon.typeValue)} off`}
                    </p>
                    {coupon.productIds.length > 0 && (
                      <p className="coupon-products">
                        *Valid on selected products only
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleApplyCoupon(coupon)}
                    className="apply-coupon-btn"
                  >
                    APPLY
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-coupons-message">
              <p>No applicable coupons available for your selected items.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="checkout-container">
      <div className="checkout-progress">
        <div
          className="progress-step"
          onClick={() => {
            sessionStorage.setItem("fromPayment", "true");
            navigate("/address");
          }}
        >
          ADDRESS
        </div>
        <div className="progress-separator">........</div>
        <div className="progress-step active">CHECKOUT</div>
      </div>

      <div className="payment-content">
        <div className="payment-section">
          <div className="payment-selected-address">
            <p>
              {savedAddress.firstName} {savedAddress.lastName}
            </p>
            <p>{savedAddress.addressLine1},</p>
            {savedAddress.addressLine2 && <p>{savedAddress.addressLine2},</p>}
            <p>{`${savedAddress.city}, ${savedAddress.state} ${savedAddress.postalCode}`}</p>
            <p>{`${savedAddress.phoneCode}${savedAddress.phoneNumber}`}</p>
            <div className="payment-address-change">
              <button
                className="payment-change-address-btn"
                onClick={handleChangeAddress}
              >
                CHANGE ADDRESS
              </button>
            </div>
          </div>

          <div className="payment-selected-items">
            <div className="payment-items-header">
              <label className="payment-item-checkbox">
                <span>{selectedItemsCount} ITEM SELECTED</span>
              </label>
              <div className="payment-item-actions">
                <button onClick={handleRemoveSelectedItems}>REMOVE</button>
                <span></span>
                <button onClick={handleMoveToWishlist}>MOVE TO WISHLIST</button>
              </div>
            </div>

            {cartItems.map(
              (item) =>
                selectedItems[item.id] && (
                  <div key={item.id} className="payment-item">
                    <img src={item.images[0]} alt={item.name} />
                    <div className="payment-item-details">
                      <h4>{item.name}</h4>
                      <h3>in {item.productCategory || item.categoryName}</h3>
                      <p>Qty: {item.quantity}</p>
                      <div className="payment-item-price">
                        <div className="payment-item-price-details">
                          <span className="original-price">
                            M.R.P: ${formatPrice(parseFloat(item.actualPrice))}
                          </span>
                          {Math.round(
                            ((parseFloat(item.actualPrice) -
                              parseFloat(item.sellingPrice)) /
                              parseFloat(item.actualPrice)) *
                            100
                          ) > 0 && (
                              <span className="checkout1-discount">
                                {Math.round(
                                  ((parseFloat(item.actualPrice) -
                                    parseFloat(item.sellingPrice)) /
                                    parseFloat(item.actualPrice)) *
                                  100
                                )}
                                % OFF
                              </span>
                            )}
                        </div>
                        <span className="current-price">
                          ${formatPrice(parseFloat(item.sellingPrice))}
                        </span>
                      </div>
                    </div>
                  </div>
                )
            )}
          </div>
        </div>

        <div className="payment-right-section">
          <div className="payment-coupons">
            <div className="payment-coupons-header">
              <h3>COUPONS</h3>
            </div>
            <div className="payment-coupons-content">
              <div className="payment-coupon-icon">
                <MdLocalOffer />
              </div>
              <div className="payment-coupon-text">
                {selectedCoupon ? (
                  <span>
                    {selectedCoupon.name} applied
                    <button
                      className="remove-coupon"
                      onClick={() => setSelectedCoupon(null)}
                    >
                      REMOVE
                    </button>
                  </span>
                ) : (
                  "Apply Coupons"
                )}
              </div>
              <button
                className="payment-apply-btn"
                onClick={() => setShowCouponsModal(true)}
              >
                {selectedCoupon ? "CHANGE" : "APPLY"}
              </button>
            </div>
          </div>

          <div className="checkout-payment-price-details">
            <h2>PRICE DETAILS ({selectedItemsCount} Item)</h2>
            <PriceBreakdown />
          </div>

          <div className="payment-choose-payment">
            <h3>Choose Payment Mode</h3>
            <div className="checkout-payment-options">
              <label className="checkout-payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="cod"
                  checked={paymentMethod === "cod"}
                  onChange={handlePaymentMethodChange}
                />
                <span>Cash</span>
              </label>
              <label className="checkout-payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="online"
                  checked={paymentMethod === "online"}
                  onChange={handlePaymentMethodChange}
                />
                <span>Online payment</span>
              </label>
              <label className="checkout-payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="etf"
                  checked={paymentMethod === "etf"}
                  onChange={handlePaymentMethodChange}
                />
                <span>ETF Payment</span>
              </label>
            </div>
          </div>

          {paymentMethod === "etf" && (
            <div className="bank-details-card">
              <h3 className="bank-details-title">Bank Details</h3>
              <div className="bank-details-content">
                <p>
                  <strong>Bank Name:</strong> {bankDetails.bankName}
                </p>
                <p>
                  <strong>Account No:</strong> {bankDetails.accountNo}
                </p>
                <p>
                  <strong>IFSC:</strong> {bankDetails.ifsc}
                </p>
                <p>
                  <strong>Branch:</strong> {bankDetails.branch}
                </p>
              </div>
            </div>
          )}

          <div className="payment-place-order-btn-container">
            <button
              className="payment-place-order-btn"
              onClick={handlePlaceOrder}
            >
              PLACE ORDER
            </button>
          </div>
        </div>
      </div>

      {showErrorPopup && (
        <div className="error-popup-overlay">
          <div className="error-popup">
            <button
              className="error-close-popup"
              onClick={() => {
                setShowErrorPopup(false);
                if (selectedItemsCount === 0) {
                  navigate("/");
                }
              }}
            >
              ×
            </button>
            <div className="error-popup-content">
              <div className="error-icon">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png"
                  alt="error-icon"
                  className="error-icon-img"
                />
              </div>
              <h2>Oops!</h2>
              <p className="error-message">
                {selectedItemsCount === 0
                  ? "Please select at least one item to proceed"
                  : "There was an error placing your order. Please try again."}
              </p>
            </div>
          </div>
        </div>
      )}

      {showOrderPopup && (
        <div className="order-popup-overlay">
          <div className="order-popup">
            <button
              className="close-popup"
              onClick={() => setShowOrderPopup(false)}
            >
              ×
            </button>
            <div className="popup-content">
              <div className="box-icon">
                <img
                  src="https://s3-alpha-sig.figma.com/img/b523/1db6/240fd191f3f9ca65c691eddf82b71a51?Expires=1744588800&Key-Pair-Id=APKAQ4GOSFWCW27IBOMQ&Signature=X7fxLixdavAkEeGIAF7elB7yYPeXomLltGvDLsNzBNj3MIEVP9FNsJS9dbjp41AvNFuQ68yDqgmi3YpY3jhKlCMAjnij-2GKlbpAOUsBRs9mBYcE~1QG68ZGCixZF9vrdcKvfQB9Ar643XzrrJR-WFNfdDymWxVLnhDRugg~xhAai-ccXMstbqfMAnpwNkmuKMecqsqf8BeSzm8T0J6Np0YxLPvMZuqZWS1W~y5L3KDT4037qk7-1Iy~ZZ8DRXakFnHoMZJBhPFx7LeqxKBgo8mZ70-Bdhh58OXRMPH5FdkeQpYq2PFpSlAO-dCyl0L2F12JiicrA5w6CQ1ab-TXzg__"
                  alt="box-icon"
                  className="box-icon-img"
                />
              </div>
              <h2>Woohoo!</h2>
              <p className="order-message">Your order has been placed!</p>
              <p className="reference">Reference# - 002349876</p>
              <p className="relax-message">
                Pull up a chair, sit back and relax
                <br />
                as your order is on its way to you!
              </p>
              <div className="progress-bar"></div>
            </div>
          </div>
        </div>
      )}

      {showRemoveConfirmation && (
        <div className="error-popup-overlay">
          <div className="error-popup">
            <button
              className="error-close-popup"
              onClick={() => setShowRemoveConfirmation(false)}
            >
              ×
            </button>
            <div className="error-popup-content">
              <div className="error-icon">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png"
                  alt="confirm-icon"
                  className="error-icon-img"
                />
              </div>
              <h2>Confirm Remove</h2>
              <p className="error-message">
                Are you sure you want to remove selected items?
              </p>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button
                  className="payment-place-order-btn"
                  style={{ width: "120px", padding: "8px" }}
                  onClick={confirmRemove}
                >
                  Yes, Remove
                </button>
                <button
                  className="payment-change-address-btn"
                  style={{ width: "120px", padding: "8px" }}
                  onClick={() => setShowRemoveConfirmation(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMoveConfirmation && (
        <div className="error-popup-overlay">
          <div className="error-popup">
            <button
              className="error-close-popup"
              onClick={() => setShowMoveConfirmation(false)}
            >
              ×
            </button>
            <div className="error-popup-content">
              <div className="error-icon">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png"
                  alt="confirm-icon"
                  className="error-icon-img"
                />
              </div>
              <h2>Confirm Move</h2>
              <p className="error-message">Move selected items to wishlist?</p>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button
                  className="payment-place-order-btn"
                  style={{ width: "120px", padding: "8px" }}
                  onClick={confirmMove}
                >
                  Yes, Move
                </button>
                <button
                  className="payment-change-address-btn"
                  style={{ width: "120px", padding: "8px" }}
                  onClick={() => setShowMoveConfirmation(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStripePopup && (
        <div className="error-popup-overlay">
          <div className="error-popup">
            <button
              className="error-close-popup"
              onClick={() => setShowStripePopup(false)}
            >
              ×
            </button>
            <div className="error-popup-content">
              <h2>Online Payment</h2>
              <p className="error-message">
                You will be redirected to Stripe's secure payment page.
              </p>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button
                  className="payment-place-order-btn"
                  style={{ width: "120px", padding: "8px" }}
                  onClick={handleStripePayment}
                >
                  Proceed
                </button>
                <button
                  className="payment-change-address-btn"
                  style={{ width: "120px", padding: "8px" }}
                  onClick={() => setShowStripePopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div className="order-popup-overlay">
          <div className="order-popup">
            <div className="popup-content">
              <div className="box-icon">
                <img
                  src="https://s3-alpha-sig.figma.com/img/b523/1db6/240fd191f3f9ca65c691eddf82b71a51?Expires=1738540800&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4&Signature=NnaeQ49TPjV-qSG2OaHPcW~m4qjfyrNChXE2DQvJ-cJqkNuB7SULVO3g1AJ8xC0R1J3pHz0wWO3RGgbHbKCUzmzm2B9klfe6oabLUVfNwvobZl80ft5U0kEC6erIsjyJMHEIJePGms7LeyRci15Qj-Xdax-CkPOuPSqK8g4rVxZ8xd04iQdftwFfaYDLyZfYB~gNNX9Cu~LAgx0kWPH6U1wH-9xWXLaL8N3xImQRF7wdZRPKbrjxExdMhhL8pxCfCfZE0eVbFDAkjPrlw6heAaqS0A-KTczE1CVUtJswGsx98so2hvwNttP84pvqn6uxtdPg9iFI5xDcX-lhDfw7sA__"
                  alt="success-icon"
                  className="box-icon-img"
                />
              </div>
              <h2>Payment Successful!</h2>
              <p className="order-message">Your order has been placed!</p>
              <p className="reference">Reference# - {paymentSessionId}</p>
              <p className="relax-message">
                Pull up a chair, sit back and relax
                <br />
                as your order is on its way to you!
              </p>
              <button
                className="payment-place-order-btn"
                onClick={() => {
                  setShowSuccessPopup(false);
                  navigate("/");
                }}
              >
                CONTINUE SHOPPING
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelPopup && (
        <div className="error-popup-overlay">
          <div className="error-popup">
            <button
              className="error-close-popup"
              onClick={() => setShowCancelPopup(false)}
            >
              ×
            </button>
            <div className="error-popup-content">
              <div className="error-icon">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1828/1828843.png"
                  alt="cancel-icon"
                  className="error-icon-img"
                />
              </div>
              <h2>Payment Cancelled</h2>
              <p className="error-message">
                Your payment was cancelled. You can try again or choose a
                different payment method.
              </p>
              <button
                className="payment-place-order-btn"
                onClick={() => setShowCancelPopup(false)}
              >
                CONTINUE CHECKOUT
              </button>
            </div>
          </div>
        </div>
      )}

      {showCouponsModal && <CouponsModal />}
    </div>
  );
};

export default Checkout;
